import { updateCommunication } from "@/api/edit-comunications/edit-comunications";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form, FormControl } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DynamicFormField } from "@/components/client/dynamic-field.jsx";
import { calculateCommunicationDeadline } from "@/lib/communication-deadline";
import { useStore } from "@/store";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Loader2, AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";

export function EditDetailsForm({ fetchData }) {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const getStates = useStore((state) => state.getStates);
  const getTags = useStore((state) => state.getTags);
  const { states, tags, currentCommunication } = useStore((state) => ({
    states: state.states || [],
    tags: state.tags || [],
    currentCommunication: state.currentCommunication,
  }));
  const companyData = useStore((state) => state.companyData);

  const [formData, setFormData] = useState({
    state_id: "",
    risk: "medium",
    tags: [],
  });

  const [tagsLoading, setTagsLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    defaultValues: formData,
  });

  useEffect(() => {
    if (currentCommunication) {
      try {
        const initialData = {
          state_id: currentCommunication.state?.id || "",
          risk: currentCommunication.risk || "medium",
          tags: currentCommunication.tags?.map((tag) => tag?.id) || [],
        };

        if (currentCommunication.form?.questions) {
          currentCommunication.form.questions
            .filter((question) => question && question?.visibility?.list === true)
            .forEach((question) => {
              const existingAnswer = currentCommunication.answers?.find(
                (answer) => answer?.question?.alias === question?.alias,
              );
              initialData[question.alias] = existingAnswer?.answerText || "";
            });
        }

        setFormData(initialData);
        form.reset(initialData); // Resetear el formulario con los nuevos valores
      } catch (err) {
        console.error("Error al inicializar el formulario:", err);
        setError(t("errors.formInitialization"));
      }
    }
  }, [currentCommunication, form, t]);

  useEffect(() => {
    const loadTags = async () => {
      setTagsLoading(true);
      setError(null);
      try {
        await getTags();
      } catch (err) {
        console.error("Error al cargar etiquetas:", err);
        setError(t("errors.loadingTags"));
      } finally {
        setTagsLoading(false);
      }
    };

    loadTags();
  }, [getTags, t]);

  useEffect(() => {
    const loadStates = async () => {
      setStatesLoading(true);
      setError(null);
      try {
        await getStates();
      } catch (err) {
        console.error("Error al cargar estados:", err);
        setError(t("errors.loadingStates"));
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  }, [getStates, t]);

  const listQuestions = currentCommunication?.form?.questions?.filter(
    (question) => question?.visibility?.list === true,
  ) || [];

  useEffect(() => {
    if (listQuestions?.length > 0) {
      try {
        listQuestions.forEach((question) => {
          const existingAnswer = currentCommunication.answers?.find(
            (answer) => answer?.question?.alias === question?.alias,
          );
          form.register(question.alias, {
            // Los campos `required` son obligatorios en el formulario público, pero no
            // cuando un administrador edita la comunicación desde el panel de administración.
            required: false,
            value: existingAnswer?.answerText || "",
          });
        });
      } catch (err) {
        console.error("Error al registrar campos del formulario:", err);
      }
    }
  }, [form, listQuestions, currentCommunication?.answers]);

  const handleChange = (field, value) => {
    try {
      setFormData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
      form.setValue(field, value);
    } catch (err) {
      console.error(`Error al cambiar el campo ${field}:`, err);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const formValues = form.getValues();
      const updatedFormData = { ...formValues };

      await updateCommunication(currentCommunication?.id, updatedFormData);
      toast.success(t("alertDialog.toast.updatedSuccess"));
      
      if (typeof fetchData === 'function') {
        await fetchData();
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      toast.error(t("alertDialog.toast.updatedError"));
      setError(t("errors.savingChanges"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentCommunication) {
    return (
      <div className="border border-dashed rounded-lg p-6 border-primary">
        <div className="flex flex-col items-center py-4">
          <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
          <p>{t("errors.noDataAvailable")}</p>
        </div>
      </div>
    );
  }

  const communicationDeadline = calculateCommunicationDeadline(
    currentCommunication,
    companyData,
  );

  return (
    <>
      <div className="border border-dashed rounded-lg p-6 border-primary">
        <Toaster />
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <div>
          <span>{t("edit.editForm.identificationNumber")} </span>
          <span>{currentCommunication?.identificationNumber || t("general.notAvailable")}</span>
        </div>
        <div>
          <span>{t("edit.editForm.date")}: </span>
          <span data-testid="communication-date">
            {currentCommunication?.date
              ? format(
                  new Date(currentCommunication.date),
                  "dd '-' MM '-' yyyy HH:mm:ss",
                  {
                    locale: es,
                  }
                )
              : t("general.notAvailable")}
          </span>
        </div>
        <div>
          <span>{t("headColumns.communications.limitDate")}: </span>
          <span data-testid="communication-limit-date">
            {communicationDeadline?.limitDate
              ? format(communicationDeadline.limitDate, "dd '-' MM '-' yyyy", {
                  locale: es,
                })
              : t("general.notAvailable")}
          </span>
        </div>
        <div
          className={`inline-block mt-2 p-2 rounded ${
            communicationDeadline?.colors?.bg || "bg-gray-300"
          } ${communicationDeadline?.colors?.text || "text-black"}`}
          data-testid="communication-deadline-days"
        >
          {communicationDeadline
            ? `${communicationDeadline.days} días`
            : t("general.notAvailable")}
        </div>

        <Form {...form} key={currentCommunication?.id || 'form'}>
          <div className="w-[50%] text-black-500 mb-4">
            <label htmlFor="state_id">{t("edit.editForm.state")}</label>
            <FormControl>
              {statesLoading ? (
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span>{t("general.loading")}</span>
                </div>
              ) : (
                <Select
                  onValueChange={(value) => handleChange("state_id", value)}
                  value={formData.state_id}
                >
                  <SelectTrigger id="state_id">
                    <SelectValue placeholder={t("edit.editForm.selectState")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(states || [])
                      .filter((state) => state?.type === currentCommunication?.type)
                      .map((item) => (
                        <SelectItem key={item?.id || Math.random()} value={item?.id || ''}>
                          {item?.names?.find(
                            (name) => name?.language === i18n.language,
                          )?.text || item?.names?.[0]?.text || t("general.notAvailable")}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </FormControl>
          </div>

          <div className="w-[50%] text-black-500 mb-4">
            <label htmlFor="risk">{t("edit.editForm.risk")}</label>
            <FormControl>
              <Select
                onValueChange={(value) => handleChange("risk", value)}
                value={formData.risk}
              >
                <SelectTrigger id="risk">
                  <SelectValue placeholder={t("edit.editForm.selectRisk")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    {t("edit.editForm.lowRisk")}
                  </SelectItem>
                  <SelectItem value="medium">
                    {t("edit.editForm.mediumRisk")}
                  </SelectItem>
                  <SelectItem value="high">
                    {t("edit.editForm.highRisk")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </div>

          <div className="w-[50%] text-black-500 mb-4">
            <label htmlFor="tags">{t("edit.editForm.tags")}</label>
            <FormControl>
              {tagsLoading ? (
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span>{t("general.loading")}</span>
                </div>
              ) : (
                <div className="border rounded p-2">
                  {(tags || [])
                    .filter((tag) => tag?.type === currentCommunication?.type)
                    .map((tag) => (
                      <div key={tag?.id || Math.random()} className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={formData.tags?.includes(tag?.id)}
                          onChange={() =>
                            handleChange(
                              "tags",
                              formData.tags?.includes(tag?.id)
                                ? formData.tags.filter((id) => id !== tag?.id)
                                : [...(formData.tags || []), tag?.id],
                            )
                          }
                        />
                        <span className="ml-2">{tag?.text || t("general.notAvailable")}</span>
                      </div>
                    ))}
                </div>
              )}
            </FormControl>
          </div>

          {listQuestions?.length > 0 && (
            <div className="mt-4">
              {listQuestions.map((question) => (
                <DynamicFormField
                  key={question?.alias || Math.random()}
                  question={question}
                  form={form}
                  disabled={false}
                  showRequiredMark={false}
                  defaultValue={form.getValues(question?.alias) || ''}
                  onChange={(value) => handleChange(question?.alias, value)}
                />
              ))}
            </div>
          )}

          <div className="mt-4">
            {isSaving ? (
              <Button disabled className="mr-2">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("buttonTexts.saving")}
              </Button>
            ) : (
              <Button type="button" onClick={handleSave}>
                {t("buttonTexts.save")}
              </Button>
            )}
          </div>
        </Form>
      </div>

      {/* Nueva sección de Cambios de Estado */}
      {companyData?.hideCommunicationStateLog !== true &&
        currentCommunication?.stateLogs &&
        currentCommunication.stateLogs.length > 0 && (
        <div className="border border-dashed text-black rounded-lg my-6 p-6 border-primary">
          <Card className="mb-4 my-6 text-black">
            <CardHeader>
              <div className="flex p-3">Cambios de Estado:</div>
            </CardHeader>
            <hr />
            <CardContent>
              <div className="space-y-3">
                {currentCommunication.stateLogs.map((stateLog, index) => (
                  <div key={stateLog.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{stateLog.originalStateName}</span>
                    <span className="text-gray-500">→</span>
                    <span className="font-medium">{stateLog.newStateName}</span>
                    <span className="text-sm text-gray-600 ml-auto">
                      ({stateLog.daysSinceLastChange} días)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="border border-dashed text-black rounded-lg my-6 p-6 border-primary">
        <Card className="mb-4 my-6 text-black">
          <CardHeader>
            <div className="flex p-3">{t("edit.editForm.questions")}:</div>
          </CardHeader>
          <hr />
          <CardContent>
            {currentCommunication?.form?.questions.map((question, index) => {
              const answer = currentCommunication.answers?.find(
                  (ans) => ans.question.id === question.id,
              );

              let answerText = null;

              if (answer) {
                if (question.type === "select" || question.type === "radio") {
                  // Verificar que `question.options` sea un array antes de buscar la opción seleccionada
                  const selectedOption = Array.isArray(question.options)
                      ? question.options.find((option) => option.value === answer.answerText)
                      : null;

                  if (selectedOption) {
                    const esText = Array.isArray(selectedOption.text)
                        ? selectedOption.text.find((text) => text.language === "es")
                        : null;

                    if (esText) {
                      answerText = esText.text;
                    } else {
                      return null; // Texto en español no encontrado
                    }
                  } else {
                    return null; // Opción seleccionada no encontrada
                  }
                } else if (question.type === "terms") {
                  answerText = answer.answerText === "1" ? "Sí" : "No"; // Respuesta de tipo términos
                } else {
                  answerText = answer.answerText; // Texto directo
                }
              }


              if (answerText) {
                return (
                    <div key={index}>
                      <p>
                        <strong>
                          {Array.isArray(question?.texts) // Verifica si `texts` es un array
                              ? question.texts.find((text) => text.language === i18n.language)?.text || "Pregunta"
                              : "Pregunta"} {/* Valor predeterminado si `texts` no es un array */}
                        </strong>
                      </p>
                      <p>{answerText}</p>
                    </div>
                );
              }

              return null;
            })}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
